/* Animaciones de entrada del hero (GSAP).
 *
 * Tres variantes seleccionables desde el editor (setting `animation` de la
 * sección Hero) o, para comparar en desarrollo, con `?anim=a|b|c` en la URL.
 * **B es la elegida** (Robert, 2026-08-18) y por tanto la de por defecto; A y C
 * se conservan seleccionables por si el cliente quiere cambiar sin tocar código.
 *
 *   a — "Reveal cinematográfico": el titular se descubre por máscara palabra a
 *       palabra, el resto sube encadenado y la tabla entra con un empuje suave.
 *   b — "Palabras y cristal" (ACTIVA): las palabras del titular caen con rebote,
 *       el subtítulo entra desenfocado y la card de best sellers llega desde la
 *       derecha girando levemente.
 *   c — "Zoom de apertura": el fondo arranca ampliado y se asienta, el bloque
 *       de texto sube en conjunto y los cortes de la tabla aparecen con sus
 *       puntos encendiéndose uno a uno.
 *
 * gsap.min.js / ScrollTrigger.min.js los carga hero.liquid (defer) antes que
 * este archivo. Sin GSAP —o con prefers-reduced-motion— no se toca nada: el
 * CSS ya deja el hero visible y completo por defecto.
 */
(function heroIntro() {
  var hero = document.querySelector('[data-hero]');
  if (!hero) return;
  /* Sin GSAP o con reduced-motion no hay intro: el hero ya está en su estado
     final, así que se marca listo de una y el hover recupera su transición. */
  if (typeof gsap === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    hero.classList.add('is-intro-done');
    return;
  }

  var params = new URLSearchParams(window.location.search);
  var variant = (params.get('anim') || hero.getAttribute('data-hero-anim') || 'b').toLowerCase();
  if (['a', 'b', 'c'].indexOf(variant) === -1) variant = 'b';

  var title = hero.querySelector('[data-hero-title]');
  var subtitle = hero.querySelector('.hero__subtitle');
  var ctas = hero.querySelectorAll('.hero__ctas .btn');
  var board = hero.querySelector('[data-hero-board]');
  var glass = hero.querySelector('.hero__glass');
  /* Columna derecha del hero: la card del fundador (v2) o, si el merchant la
     apaga en el editor, la card de best sellers del v1. Las dos entran igual y
     nunca se ven a la vez en el mismo ancho — la que esté oculta por CSS recibe
     los estilos inline de GSAP sin que se note. Se anima el contenedor, no el
     retrato ni la placa. */
  var rightCol = [hero.querySelector('.hero__founder'), hero.querySelector('.hero__aside')].filter(Boolean);
  /* Se anima el CONTENEDOR del hotspot, no el punto: el punto tiene su propio
     pulso CSS infinito (`hero-dot-breathe`) sobre `transform`, y encima su
     opacidad la controla el hover para cederle el sitio a la etiqueta. Si GSAP
     escribiera opacity/scale inline sobre el punto, ganaría a esas dos reglas y
     los puntos se quedarían invisibles y sin reaccionar al hover. Además aquí
     solo se anima `opacity`: el zone lleva `translate(-50%,-50%)` en CSS y un
     scale de GSAP lo descolocaría. */
  var zones = hero.querySelectorAll('.hero__zone');
  var bg = hero.querySelector('.hero__bg-image');

  /* Parte el titular en palabras envueltas en una máscara, para poder
     descubrirlas de una en una sin depender del plugin de pago SplitText.
     Se guarda el texto original por si hay que rehacerlo. */
  function splitWords(el) {
    if (!el || el.dataset.split === '1') return [];
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    var inners = words.map(function (word, i) {
      var mask = document.createElement('span');
      mask.className = 'hero__word';
      var inner = document.createElement('span');
      inner.className = 'hero__word-inner';
      inner.textContent = word;
      mask.appendChild(inner);
      el.appendChild(mask);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return inner;
    });
    el.dataset.split = '1';
    return inners;
  }

  function run() {
    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (variant === 'a') {
      var wordsA = splitWords(title);
      tl.from(wordsA, { yPercent: 115, duration: 1, stagger: 0.08 })
        .from(subtitle, { y: 24, opacity: 0, duration: 0.7 }, '-=0.55')
        .from(ctas, { y: 18, opacity: 0, duration: 0.5, stagger: 0.1 }, '-=0.4')
        .from(glass, { opacity: 0, duration: 0.8 }, '-=0.35')
        .from(board, { y: 60, opacity: 0, duration: 0.9 }, '-=0.7')
        .from(rightCol, { y: 40, opacity: 0, duration: 0.7 }, '-=0.6')
        .from(zones, { opacity: 0, duration: 0.4, stagger: 0.06 }, '-=0.3');
    } else if (variant === 'b') {
      var wordsB = splitWords(title);
      tl.from(wordsB, {
        yPercent: -120,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'back.out(1.7)'
      })
        .from(subtitle, { opacity: 0, filter: 'blur(12px)', y: 10, duration: 0.8 }, '-=0.4')
        .from(ctas, { scale: 0.85, opacity: 0, duration: 0.45, stagger: 0.12, ease: 'back.out(2)' }, '-=0.45')
        .from(board, { x: -70, opacity: 0, duration: 0.9 }, '-=0.5')
        .from(glass, { opacity: 0, scale: 0.94, transformOrigin: 'left center', duration: 1 }, '-=0.9')
        .from(rightCol, { x: 90, rotation: 3, opacity: 0, duration: 0.8, ease: 'power4.out' }, '-=0.75')
        .from(zones, { opacity: 0, duration: 0.35, stagger: 0.08 }, '-=0.35');
    } else {
      tl.from(bg, { scale: 1.14, duration: 1.6, ease: 'power2.out' })
        .from([title, subtitle, ctas], { y: 46, opacity: 0, duration: 0.9, stagger: 0.12 }, '-=1.3')
        .from(glass, { opacity: 0, y: 30, duration: 0.9 }, '-=0.7')
        .from(board, { scale: 0.9, opacity: 0, transformOrigin: 'center bottom', duration: 1 }, '-=0.8')
        .from(rightCol, { y: 60, opacity: 0, duration: 0.8 }, '-=0.7')
        .from(zones, { opacity: 0, duration: 0.5, stagger: 0.1 }, '-=0.45');
    }

    return tl;
  }

  var timeline = run();

  /* Al terminar la intro se limpian los transforms inline que deja GSAP sobre
     los CTA y se marca el hero como listo.

     Por qué: una transición CSS sobre una propiedad que GSAP está animando
     corrompe el valor final del tween. Con `transition: transform` en .btn los
     dos botones del hero se quedaban clavados en scale(0.85) para siempre —el
     tween terminaba pero su valor de destino ya había quedado contaminado—.
     Por eso el CSS NO le pone transición a `transform` en los CTA del hero
     hasta que aparece `.is-intro-done`, y hasta entonces GSAP es el único
     dueño de esa propiedad. */
  function introDone() {
    if (!hero || hero.classList.contains('is-intro-done')) return;
    if (typeof gsap !== 'undefined' && ctas && ctas.length) {
      gsap.set(ctas, { clearProps: 'transform,translate,rotate,scale' });
    }
    hero.classList.add('is-intro-done');
  }
  if (timeline) timeline.eventCallback('onComplete', introDone);

  /* Red de seguridad: todo lo que entra con `from` arranca en opacity 0, así que
     si la timeline no llega al final el hero se queda MEDIO INVISIBLE. Pasa de
     verdad cuando la página se abre en una pestaña en segundo plano: el
     navegador congela requestAnimationFrame y GSAP no avanza. Al volver a la
     pestaña —o pasados 8s— se salta al estado final si aún no se completó. */
  function settle() {
    if (timeline && timeline.progress() < 1) timeline.progress(1);
    introDone();
  }
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) setTimeout(settle, 3000);
  });
  setTimeout(settle, 8000);

  /* Parallax de la tabla al hacer scroll (venía de Task 4, se conserva). */
  if (board && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    gsap.to(board, {
      yPercent: -8,
      ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top bottom', end: 'bottom top', scrub: true }
    });
  }

  /* Modo comparación: con `?anim=` en la URL aparece un botón para relanzar la
     entrada y saltar entre variantes sin recargar a mano. Nunca se imprime en
     una visita normal. */
  if (params.has('anim')) {
    /* La timeline queda accesible para poder pararla en un punto exacto al
       comparar variantes (capturas fotograma a fotograma). */
    window.__heroTL = timeline;
    var bar = document.createElement('div');
    bar.className = 'hero-anim-demo';
    bar.innerHTML =
      '<span class="hero-anim-demo__label">Hero intro <b>' + variant.toUpperCase() + '</b></span>' +
      '<button type="button" data-replay>Replay</button>' +
      '<a href="?anim=a">A</a><a href="?anim=b">B</a><a href="?anim=c">C</a>';
    document.body.appendChild(bar);
    bar.querySelector('[data-replay]').addEventListener('click', function () {
      timeline.kill();
      gsap.set([title, subtitle, ctas, board, glass, zones, bg].concat(rightCol), { clearProps: 'all' });
      timeline = run();
      window.__heroTL = timeline;
    });
  }
})();
